import React, { useState, useEffect } from 'react';
import { SignIn, SignUp } from '@clerk/clerk-react';

import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

interface AuthModalProps {
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
  initialAuthMode?: 'signIn' | 'signUp';

}

export const AuthModal: React.FC<AuthModalProps> = ({
  showAuthModal,
  setShowAuthModal,
  initialAuthMode = 'signIn',
}) => {
  const { t } = useTranslation();
  const [authMode, setAuthMode] = useState<'signIn' | 'signUp'>(initialAuthMode);
  const [mounted, setMounted] = useState(false);

  // Trigger entrance animation
  useEffect(() => {
    if (showAuthModal) {
      requestAnimationFrame(() => setMounted(true));
    } else {
      setMounted(false);
    }
  }, [showAuthModal]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = showAuthModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showAuthModal]);

  // Form validation and H1 rewriting for Clerk forms inside the modal
  useEffect(() => {
    if (!showAuthModal) return;

    let isFormValid = false;

    const handleFormSubmit = (e: Event) => {
      if (!isFormValid) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const checkFormValidity = () => {
      const inputs = document.querySelectorAll<HTMLInputElement>('.auth-body .cl-formFieldInput');
      const button = document.querySelector<HTMLButtonElement>('.auth-body .cl-formButtonPrimary');
      if (!button) return;

      let isValid = true;
      let hasInputs = false;
      inputs.forEach(input => {
        hasInputs = true;
        const value = input.value.trim();
        
        // Field is required if marked so, or if it is a password/email/identifier field
        const isRequired = input.required || input.hasAttribute('required') || input.type === 'password' || input.type === 'email' || input.name === 'identifier';
        if (isRequired && !value) {
          isValid = false;
        }
        
        // Email check
        if (value && (input.type === 'email' || input.name === 'identifier')) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            isValid = false;
          }
        }
        
        // Password check (min 8 chars)
        if (value && input.type === 'password' && value.length < 8) {
          isValid = false;
        }
      });

      if (!hasInputs) {
        isValid = false;
      }

      isFormValid = isValid;

      if (isValid) {
        button.removeAttribute('disabled');
        button.style.opacity = '1';
        button.style.pointerEvents = 'auto';
      } else {
        button.setAttribute('disabled', 'true');
        button.style.opacity = '0.5';
        button.style.pointerEvents = 'none';
      }
    };

    const observer = new MutationObserver(() => {
      // Temporarily disconnect to avoid infinite mutation loops during DOM modifications
      observer.disconnect();

      // Convert Clerk H1 titles to H2 to ensure exactly one H1 per page for SEO
      const clerkH1s = document.querySelectorAll('.auth-body .cl-rootBox h1, .auth-body .cl-card h1');
      clerkH1s.forEach(h1 => {
        const h2 = document.createElement('h2');
        Array.from(h1.attributes).forEach(attr => h2.setAttribute(attr.name, attr.value));
        h2.innerHTML = h1.innerHTML;
        h1.parentNode?.replaceChild(h2, h1);
      });

      checkFormValidity();
      
      const inputs = document.querySelectorAll('.auth-body .cl-formFieldInput');
      inputs.forEach(input => {
        input.removeEventListener('input', checkFormValidity);
        input.addEventListener('input', checkFormValidity);
      });

      // Prevent keyboard-enter submissions when form is invalid
      const form = document.querySelector<HTMLFormElement>('.auth-body .cl-form');
      if (form) {
        form.removeEventListener('submit', handleFormSubmit);
        form.addEventListener('submit', handleFormSubmit);
      }

      // Reconnect
      observer.observe(document.body, { childList: true, subtree: true });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    checkFormValidity();

    return () => {
      observer.disconnect();
    };
  }, [showAuthModal, authMode]);

  return (
    <AnimatePresence>
      {showAuthModal && (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <style>{`

        .auth-overlay {
          position: fixed; inset: 0; z-index: 400;
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          background: rgba(5, 8, 18, 0.85);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }

        .auth-panel {
          position: relative;
          width: 100%; max-width: 440px;
          max-height: 92vh;
          background: #0d1526;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 24px;
          overflow: hidden;
          display: flex; flex-direction: column;
          box-shadow:
            0 0 0 1px rgba(16,185,129,0.08),
            0 32px 80px rgba(0,0,0,0.6),
            0 0 60px rgba(16,185,129,0.04);
        }

        /* Top glow strip */
        .auth-panel::before {
          content: '';
          position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
          background: linear-gradient(90deg, transparent, #10B981, transparent);
          z-index: 2;
        }

        /* Corner orb */
        .auth-orb {
          position: absolute; border-radius: 50%;
          filter: blur(60px); pointer-events: none; z-index: 0;
        }

        .auth-header {
          position: relative; z-index: 1;
          padding: 28px 28px 0;
          flex-shrink: 0;
        }

        .auth-logo {
          display: flex; align-items: center; gap: 8px;
          margin-bottom: 20px;
        }
        .auth-logo-text {
          font-family: 'Outfit', sans-serif;
          font-size: 18px; font-weight: 800;
          color: #fff; letter-spacing: -0.02em;
          text-transform: uppercase;
        }

        .auth-tabs {
          display: flex;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 14px;
          padding: 4px;
          gap: 4px;
        }

        .auth-tab {
          flex: 1; padding: 9px 0;
          border: none; background: transparent;
          color: #64748b; font-size: 13px; font-weight: 600;
          font-family: var(--font-sans);
          border-radius: 10px; cursor: pointer;
          transition: color 0.2s, background 0.2s;
          letter-spacing: 0.01em;
          text-transform: uppercase;
        }
        .auth-tab:hover { color: #94a3b8; }
        .auth-tab.active {
          background: rgba(16,185,129,0.12);
          border: 1px solid rgba(16,185,129,0.25);
          color: #10B981;
        }

        .auth-body {
          position: relative; z-index: 1;
          overflow-y: auto; flex: 1;
          padding: 8px 28px 20px;
          scrollbar-width: none;
        }
        .auth-body::-webkit-scrollbar { display: none; }

        .auth-close {
          position: absolute; top: 16px; right: 16px; z-index: 10;
          width: 32px; height: 32px; border-radius: 50%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: #475569;
          transition: background 0.2s, color 0.2s, transform 0.15s;
        }
        .auth-close:hover {
          background: rgba(255,255,255,0.09);
          color: #94a3b8;
          transform: scale(1.08);
        }

        /* Clerk Overrides */
        .auth-body .cl-card {
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 12px 0 !important;
        }
        .auth-body .cl-headerTitle,
        .auth-body .cl-headerSubtitle { display: none !important; }
        .auth-body .cl-rootBox,
        .auth-body .cl-rootBox > div {
          width: 100% !important;
          display: flex !important;
          justify-content: center !important;
          margin: 0 auto !important;
        }
        .auth-body .cl-formButtonPrimary {
          background: #10B981 !important;
          color: #0A0F1E !important;
          font-weight: 700 !important;
          border-radius: 10px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          height: 52px !important;
        }
        .auth-body .cl-socialButtonsBlockButton {
          border: 1px solid rgba(255,255,255,0.1) !important;
          background: rgba(255,255,255,0.04) !important;
          border-radius: 10px !important;
          transition: all 0.2s ease !important;
          height: 52px !important;
        }
        .auth-body .cl-socialButtonsBlockButton:hover {
          background: rgba(255,255,255,0.08) !important;
          border-color: rgba(16,185,129,0.3) !important;
        }
        .auth-body .cl-socialButtonsBlockButtonText {
          color: #fff !important;
          font-weight: 600 !important;
          font-family: var(--font-sans) !important;
        }
        .auth-body .cl-socialButtonsProviderIcon {
          filter: brightness(0) invert(1) !important;
          opacity: 0.9 !important;
        }
        .auth-body .cl-formFieldInput {
          background: rgba(255,255,255,0.04) !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          color: #fff !important;
          font-family: var(--font-sans) !important;
          height: 52px !important;
          font-size: 15px !important;
          padding: 0 16px !important;
        }
        .auth-body .cl-formFieldInput::placeholder {
          color: rgba(255,255,255,0.3) !important;
        }
        .auth-body .cl-formFieldLabel {
          color: #fff !important;
          text-transform: uppercase !important;
          font-size: 11px !important;
          font-weight: 800 !important;
          letter-spacing: 0.05em !important;
          margin-bottom: 6px !important;
        }
        .auth-body .cl-formFieldLabel--optional {
          color: rgba(255,255,255,0.5) !important;
          font-weight: 600 !important;
        }
        .auth-body .cl-dividerText {
          color: #fff !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          font-size: 10px !important;
          background: transparent !important;
        }
        .auth-body .cl-formFieldErrorText,
        .auth-body .cl-formFieldWarningText {
          color: #f87171 !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          margin-top: 4px !important;
          display: block !important;
        }
        .auth-body .cl-alert {
          background-color: rgba(248, 113, 113, 0.1) !important;
          border: 1px solid rgba(248, 113, 113, 0.3) !important;
          border-radius: 8px !important;
          padding: 8px 12px !important;
          margin-bottom: 12px !important;
        }
        .auth-body .cl-alertText {
          color: #f87171 !important;
        }
        .auth-body .cl-formFieldAction {
          color: #10B981 !important;
          font-weight: 700 !important;
        }
        .auth-body .cl-footerActionLink { color: #10B981 !important; font-weight: 700 !important; }
      `}</style>

      {/* Backdrop */}
      <div
        className={`auth-overlay${mounted ? ' in' : ''}`}
        onClick={() => setShowAuthModal(false)}
      >
        {/* Panel */}
        <div
          className={`auth-panel${mounted ? ' in' : ''}`}
          onClick={e => e.stopPropagation()}
        >
          {/* Ambient orbs */}
          <div className="auth-orb" style={{ width: 260, height: 260, background: 'rgba(16,185,129,0.07)', top: -80, right: -80 }} />
          <div className="auth-orb" style={{ width: 200, height: 200, background: 'rgba(129,140,248,0.06)', bottom: -60, left: -60 }} />

          {/* Close button */}
          <button className="auth-close" onClick={() => setShowAuthModal(false)} aria-label={t('auth.close')}>
            <X size={15} />
          </button>

          {/* Header */}
          <div className="auth-header">
            {/* Tab switcher */}
            <div className="auth-tabs">
              <button
                className={`auth-tab${authMode === 'signIn' ? ' active' : ''}`}
                onClick={() => setAuthMode('signIn')}
              >
                {t('auth.sign_in')}
              </button>
              <button
                className={`auth-tab${authMode === 'signUp' ? ' active' : ''}`}
                onClick={() => setAuthMode('signUp')}
              >
                {t('auth.create_account')}
              </button>
            </div>

            {authMode === 'signIn' && (
              <p style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: '#475569', marginTop: 14, marginBottom: 4, letterSpacing: '0.01em', fontWeight: 600, textTransform: 'uppercase' }}>
                {t('auth.welcome_back')}
              </p>
            )}
          </div>

          {/* Clerk forms */}
                    
          <div className="auth-body">
            {authMode === 'signIn' ? (
              <SignIn
                routing="virtual"
                afterSignInUrl="/home"
              />
            ) : (
              <SignUp
                routing="virtual"
                afterSignUpUrl="/home"
              />
            )}
          </div>
        </div>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
  );
};
